import '@babylonjs/core/Meshes/thinInstanceMesh';
import type {Scene} from '@babylonjs/core/scene';
import {CreateGround} from '@babylonjs/core/Meshes/Builders/groundBuilder';
import {CreateLineSystem} from '@babylonjs/core/Meshes/Builders/linesBuilder';
import {DynamicTexture} from '@babylonjs/core/Materials/Textures/dynamicTexture';
import {Texture} from '@babylonjs/core/Materials/Textures/texture';
import {StandardMaterial} from '@babylonjs/core/Materials/standardMaterial';
import {Color3} from '@babylonjs/core/Maths/math.color';
import {Matrix,Quaternion,Vector3} from '@babylonjs/core/Maths/math.vector';
import {groundHeightAt,type ArenaDefinition,type ZoneState} from '@arena/shared';

const cached=new WeakMap<Scene,Map<string,DynamicTexture>>();
function texture(scene:Scene,kind:'bug'|'swarm'|'seal'):DynamicTexture{
  let cache=cached.get(scene);if(!cache){cache=new Map();cached.set(scene,cache);}
  const old=cache.get(kind);if(old)return old;
  const size=kind==='bug'?32:256;
  const t=new DynamicTexture(kind,{width:size,height:size},scene,false,Texture.NEAREST_SAMPLINGMODE);
  const g=t.getContext() as CanvasRenderingContext2D;g.clearRect(0,0,size,size);
  if(kind==='bug'){
    // A top-down pixel beetle: six legs, split wing case, head and antennae.
    g.strokeStyle='#17100d';g.lineWidth=2;
    for(const y of [11,16,21]){g.beginPath();g.moveTo(12,y);g.lineTo(6,y-3);g.lineTo(3,y+1);g.moveTo(20,y);g.lineTo(26,y-3);g.lineTo(29,y+1);g.stroke();}
    g.fillStyle='#160f0b';g.fillRect(10,9,12,17);g.fillRect(12,7,8,21);
    g.fillStyle='#69432b';g.fillRect(12,11,8,14);g.fillStyle='#a17041';g.fillRect(13,12,2,10);
    g.fillStyle='#281b14';g.fillRect(16,10,2,17);g.fillRect(12,4,8,6);
    g.strokeStyle='#49321e';g.beginPath();g.moveTo(13,5);g.lineTo(9,1);g.moveTo(19,5);g.lineTo(23,1);g.stroke();
  }else{
    const seal=kind==='seal';g.strokeStyle=seal?'#9ceaff':'#e6b368';g.lineWidth=seal?3:2;
    g.setLineDash(seal?[]:[12,8]);g.beginPath();g.arc(128,128,116,0,Math.PI*2);g.stroke();g.setLineDash([]);
    g.beginPath();g.arc(128,128,seal?95:108,0,Math.PI*2);g.stroke();
    if(seal){
      g.font='bold 18px serif';g.textAlign='center';g.textBaseline='middle';
      for(let i=0;i<12;i++){const a=i*Math.PI/6;g.save();g.translate(128+105*Math.cos(a),128+105*Math.sin(a));g.rotate(a+Math.PI/2);g.fillStyle='#b3f4ff';g.fillText(['I','V','X'][i%3]!,0,0);g.restore();}
      for(let i=0;i<6;i++){const a=i*Math.PI/3;g.beginPath();g.moveTo(128+84*Math.cos(a),128+84*Math.sin(a));g.lineTo(128+84*Math.cos(a+Math.PI*2/3),128+84*Math.sin(a+Math.PI*2/3));g.stroke();}
    }else{
      g.fillStyle='#3d231b50';g.beginPath();g.arc(128,128,106,0,Math.PI*2);g.fill();
      g.strokeStyle='#302019';for(let i=0;i<17;i++){const a=i*2.4,r=20+(i*47)%80,x=128+Math.cos(a)*r,y=128+Math.sin(a)*r;g.beginPath();g.moveTo(x-7,y+4);g.lineTo(x,y-3);g.lineTo(x+4,y+2);g.lineTo(x+9,y-7);g.stroke();}
    }
  }
  t.hasAlpha=true;t.update();cache.set(kind,t);return t;
}
function ground(scene:Scene,kind:'bug'|'swarm'|'seal'){
  const mesh=CreateGround(kind,{width:1,height:1},scene);mesh.isPickable=false;
  const material=new StandardMaterial(kind,scene);material.diffuseTexture=texture(scene,kind);material.useAlphaFromDiffuseTexture=true;material.disableLighting=true;material.emissiveColor=Color3.White();material.backFaceCulling=false;mesh.material=material;
  return {mesh,material};
}
/** One instanced draw for every crawling insect; the footprint is the real server radius. */
export class SwarmZoneView{
  private readonly field;private readonly bugs;private readonly matrices=new Float32Array(56*16);private age=0;
  constructor(scene:Scene){
    this.field=ground(scene,'swarm');this.bugs=ground(scene,'bug');this.bugs.mesh.alwaysSelectAsActiveMesh=true;
    this.bugs.mesh.thinInstanceSetBuffer('matrix',this.matrices,16,false);
  }
  update(zone:ZoneState,dt:number,arena:ArenaDefinition):void{
    this.age+=dt;const elapsed=zone.duration-zone.ticks,active=elapsed>=15;
    this.field.mesh.position.set(zone.x,zone.y+.035,zone.z);this.field.mesh.scaling.set(zone.radius*2,1,zone.radius*2);
    this.field.material.alpha=active?.65: .45+Math.sin(this.age*16)*.2;
    this.bugs.mesh.setEnabled(active);if(!active)return;
    this.bugs.material.alpha=Math.min(1,zone.ticks/10);
    for(let i=0;i<56;i++){
      const phase=i*2.39996+this.age*(i%2?1:-1)*(.35+(i%7)*.07);
      const radius=zone.radius*Math.sqrt((i+.5)/56)*(.88+Math.sin(this.age*4+i)*.07);
      const x=zone.x+Math.cos(phase)*radius,z=zone.z+Math.sin(phase)*radius;
      const y=groundHeightAt(arena,x,z,zone.y+.2)??zone.y;
      const size=.23+(i%5)*.025;
      Matrix.Compose(new Vector3(size,1,size*1.3),Quaternion.RotationAxis(Vector3.Up(),-phase+(i%2?Math.PI:0)),new Vector3(x,y+.05,z)).copyToArray(this.matrices,i*16);
    }
    this.bugs.mesh.thinInstanceBufferUpdated('matrix');
  }
  dispose():void{for(const part of [this.field,this.bugs]){part.mesh.dispose();part.material.dispose();}}
}
/** Champion-independent shackle: drawn around the existing hurt pose. */
export class AnchorView{
  private readonly seal;private readonly chains;private age=0;
  constructor(scene:Scene){
    this.seal=ground(scene,'seal');this.seal.mesh.scaling.set(1.65,1,1.65);
    const lines:Vector3[][]=[];
    for(let arm=0;arm<4;arm++)for(let link=0;link<8;link++){
      const a=arm*Math.PI/2,r=.48-link*.025,cx=Math.cos(a)*r,cz=Math.sin(a)*r;
      const points:Vector3[]=[];for(let p=0;p<=8;p++){const b=p/8*Math.PI*2,w=Math.cos(b)*.07;points.push(new Vector3(cx+Math.cos(a+link%2*Math.PI/2)*w,.08+link*.12+Math.sin(b)*.09,cz+Math.sin(a+link%2*Math.PI/2)*w));}lines.push(points);
    }
    this.chains=CreateLineSystem('virgilio-chains',{lines},scene);this.chains.color=Color3.FromHexString('#a9eaff');this.chains.isPickable=false;
  }
  update(position:Vector3,ticks:number,dt:number):void{
    this.age+=dt;this.seal.mesh.position.copyFrom(position);this.seal.mesh.position.y+=.045;
    this.seal.material.alpha=Math.min(1,ticks/5)*(.8+Math.sin(this.age*20)*.15);
    this.chains.position.copyFrom(position);this.chains.rotation.y=Math.sin(this.age*25)*.025;
  }
  dispose():void{this.seal.mesh.dispose();this.seal.material.dispose();this.chains.dispose();}
}
